import type { CommentatorRequest, GraphicInstance, GraphicKind, MatchEvent, WorldSnapshot } from '../core/contracts';

export interface LowerThirdPick { kind: GraphicKind; subjectId: string | null }

/** Player graphics the Q key can put up (and so the booth may ask for with a "Q" hint). */
export const Q_GRAPHICS: ReadonlySet<GraphicKind> = new Set<GraphicKind>([
  'goal_lower_third', 'player_lower_third', 'card_lower_third', 'sub_lower_third', 'player_stat_card',
]);

export interface LowerThirdContext {
  /** Open booth requests: one asking for a Q graphic (e.g. a player stat card) is answered first. */
  requests?: readonly CommentatorRequest[];
  /** Booth request the director focused (Tab / click) — preferred over the others. */
  focusId?: number | null;
  /** Graphics currently on air (a request already satisfied on air is skipped). */
  onAir?: readonly Pick<GraphicInstance, 'kind' | 'subjectId'>[];
}

/** The open booth request Q should answer, if any (focused first, then soonest to expire). */
export function boothGraphicRequest(ctx: LowerThirdContext): CommentatorRequest | null {
  const onAir = ctx.onAir ?? [];
  const wanted = (ctx.requests ?? []).filter((r) => !r.resolved && !!r.wants.graphic && Q_GRAPHICS.has(r.wants.graphic)
    && !!r.wants.subjectId && !onAir.some((g) => g.kind === r.wants.graphic && g.subjectId === r.wants.subjectId));
  if (!wanted.length) return null;
  return wanted.find((r) => r.id === ctx.focusId) ?? [...wanted].sort((a, b) => a.expiresAt - b.expiresAt)[0]!;
}

/** How recent (sim seconds) a goal / card / sub must be to drive the context lower third. */
export const LOWER_THIRD_RECENT_S = 20;

/**
 * Context-aware lower third (Q key):
 *  open booth request for a player graphic → exactly what the booth asked for (stat card / name)
 *  half-time / full-time phase → the half-time / full-time score panel (subjectId null)
 *  goal within 20s → goal_lower_third (scorer)
 *  card within 20s → card_lower_third (carded player)
 *  sub within 20s  → sub_lower_third (player coming on = event.playerId)
 *  else            → player_lower_third on the ball carrier (or nearest player to the ball).
 */
export function pickLowerThird(
  events: readonly MatchEvent[], snapshot: WorldSnapshot, recentS = LOWER_THIRD_RECENT_S, ctx: LowerThirdContext = {},
): LowerThirdPick {
  const req = boothGraphicRequest(ctx);
  if (req) return { kind: req.wants.graphic!, subjectId: req.wants.subjectId! };
  if (snapshot.phase === 'half_time') return { kind: 'half_time_score', subjectId: null };
  if (snapshot.phase === 'full_time') return { kind: 'full_time_score', subjectId: null };
  let goal: MatchEvent | null = null, card: MatchEvent | null = null, sub: MatchEvent | null = null;
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i]!;
    if (snapshot.t - e.t > recentS) break;
    if (e.t > snapshot.t) continue;
    if (!goal && e.type === 'goal' && e.playerId) goal = e;
    else if (!card && (e.type === 'yellow_card' || e.type === 'red_card') && e.playerId) card = e;
    else if (!sub && e.type === 'substitution' && e.playerId) sub = e;
  }
  if (goal) return { kind: 'goal_lower_third', subjectId: goal.playerId };
  if (card) return { kind: 'card_lower_third', subjectId: card.playerId };
  if (sub) return { kind: 'sub_lower_third', subjectId: sub.playerId };
  return { kind: 'player_lower_third', subjectId: ballCarrier(snapshot) };
}

/** Ball owner, else the player nearest the ball (null with no players). */
export function ballCarrier(snapshot: WorldSnapshot): string | null {
  if (snapshot.ball.ownerId) return snapshot.ball.ownerId;
  let best: string | null = null, bestD = Infinity;
  const b = snapshot.ball.pos;
  for (const p of snapshot.players) {
    const d = (p.pos.x - b.x) ** 2 + (p.pos.z - b.z) ** 2;
    if (d < bestD) { bestD = d; best = p.id; }
  }
  return best;
}
